import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SimulationFrameContext } from '../context/SimulationFrameContext';
import styles from '../pages/DisplayPage.module.css';

const IFRAME_SRC_DOC = `<!DOCTYPE html>
<html>
  <head>
    <meta name="viewport" content="width=1280, height=720, initial-scale=1">
  </head>
  <body><div id="sim-root"></div></body>
</html>`;

function simViewportCss(width: number, height: number): string {
  return `
    html, body, #sim-root,
    html.display-fullbleed, html.display-fullbleed body {
      margin: 0 !important;
      padding: 0 !important;
      width: ${width}px !important;
      height: ${height}px !important;
      min-width: ${width}px !important;
      min-height: ${height}px !important;
      max-width: ${width}px !important;
      max-height: ${height}px !important;
      overflow: hidden !important;
      background: #000;
    }
    #sim-root { position: relative; }
    .displayContainer {
      width: 100% !important;
      height: 100% !important;
      min-height: 0 !important;
      max-height: 100% !important;
    }
  `;
}

function syncParentStyles(iframeDoc: Document) {
  iframeDoc.head.querySelectorAll('[data-sim-copied]').forEach((node) => node.remove());
  document.head.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
    const clone = node.cloneNode(true) as HTMLElement;
    clone.setAttribute('data-sim-copied', '1');
    iframeDoc.head.appendChild(clone);
  });
  iframeDoc.documentElement.lang = document.documentElement.lang || 'pt-BR';
  iframeDoc.documentElement.className = document.documentElement.className;
  iframeDoc.body.className = document.body.className;
  iframeDoc.documentElement.classList.remove('display-fullbleed', 'tv-zoom-100');
  iframeDoc.body.classList.remove('tv-zoom-100');
  const base = iframeDoc.getElementById('sim-base-css');
  if (base) iframeDoc.head.appendChild(base);
}

function applyDesiredViewport(iframeDoc: Document, width: number, height: number) {
  iframeDoc.documentElement.style.width = `${width}px`;
  iframeDoc.documentElement.style.height = `${height}px`;
  iframeDoc.body.style.width = `${width}px`;
  iframeDoc.body.style.height = `${height}px`;
  const base = iframeDoc.getElementById('sim-base-css') ?? iframeDoc.createElement('style');
  base.id = 'sim-base-css';
  base.textContent = simViewportCss(width, height);
  if (!base.parentNode) iframeDoc.head.appendChild(base);
  let meta = iframeDoc.querySelector('meta[name="viewport"]');
  if (!meta) {
    meta = iframeDoc.createElement('meta');
    meta.setAttribute('name', 'viewport');
    iframeDoc.head.appendChild(meta);
  }
  meta.setAttribute('content', `width=${width}, height=${height}, initial-scale=1`);
}

interface SimulatedViewportFrameProps {
  width: number;
  height: number;
  scale: number;
  title?: string;
  children: React.ReactNode;
}

/**
 * Mundo simulado = width × height (iframe).
 * scale só adapta esse mundo ao monitor de quem está olhando.
 */
export const SimulatedViewportFrame: React.FC<SimulatedViewportFrameProps> = ({
  width,
  height,
  scale,
  title = 'Simulação de tela',
  children,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [mountNode, setMountNode] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let observer: MutationObserver | null = null;
    let boundDoc: Document | null = null;
    let cancelled = false;

    const relayKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      window.dispatchEvent(
        new KeyboardEvent('keydown', {
          key: event.key,
          code: event.code,
          altKey: event.altKey,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          shiftKey: event.shiftKey,
          bubbles: true,
          cancelable: true,
        })
      );
    };

    const connect = () => {
      if (cancelled) return;
      const doc = iframe.contentDocument;
      const root = doc?.getElementById('sim-root');
      if (!doc || !root) return;
      syncParentStyles(doc);
      applyDesiredViewport(doc, width, height);
      setMountNode(root);
      observer?.disconnect();
      if (boundDoc) {
        boundDoc.removeEventListener('keydown', relayKeyDown);
      }
      boundDoc = doc;
      doc.addEventListener('keydown', relayKeyDown);
      observer = new MutationObserver(() => syncParentStyles(doc));
      observer.observe(document.head, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    };

    iframe.addEventListener('load', connect);
    connect();

    return () => {
      cancelled = true;
      iframe.removeEventListener('load', connect);
      boundDoc?.removeEventListener('keydown', relayKeyDown);
      observer?.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc?.getElementById('sim-root')) return;
    applyDesiredViewport(doc, width, height);
  }, [width, height]);

  return (
    <div
      className={styles.inchSimulationFrame}
      style={{
        width: width * scale,
        height: height * scale,
      }}
    >
      <iframe
        ref={iframeRef}
        title={title}
        srcDoc={IFRAME_SRC_DOC}
        className={styles.inchSimulationIframe}
        width={width}
        height={height}
        tabIndex={-1}
        style={{
          width,
          height,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      />
      {mountNode
        ? createPortal(
            <SimulationFrameContext.Provider
              value={{ active: true, width, height }}
            >
              <div
                className={styles.displayContainer}
                style={{
                  width,
                  height,
                  minHeight: height,
                  maxHeight: height,
                }}
              >
                {children}
              </div>
            </SimulationFrameContext.Provider>,
            mountNode
          )
        : null}
    </div>
  );
};
